"use client"

import { ContextCurrencyFragment } from "@opensea/app-kit"
import type {
  ContractIdentifierInput,
  ResultOf,
  VariablesOf,
} from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import type { CombinedError, UseQueryExecute } from "urql"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../constants"

const CurrencyByContractQuery = graphql(
  `
    query currencyByContract($contract: ContractIdentifierInput!) {
      currencyByContract(contract: $contract) {
        ...ContextCurrency
      }
    }
  `,
  [ContextCurrencyFragment],
)

type UseCurrencyByContractProps = Omit<
  VariablesOf<typeof CurrencyByContractQuery>,
  "contract"
> & {
  contract: ContractIdentifierInput
  pause?: boolean
}

type UseCurrencyByContract = {
  currency: ResultOf<typeof CurrencyByContractQuery>["currencyByContract"]
  error: CombinedError | undefined
  fetching: boolean
  refetch: UseQueryExecute
}

export const useCurrencyByContract = (
  params: UseCurrencyByContractProps,
): UseCurrencyByContract => {
  const { contract, pause } = params

  const [{ data, error, fetching }, refetch] = useQuery({
    query: CurrencyByContractQuery,
    variables: {
      contract,
    },
    pause,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  return {
    currency: data?.currencyByContract ?? null,
    error,
    fetching,
    refetch: () => refetch({ requestPolicy: "network-only" }),
  }
}
